import React, { useState, useEffect, useRef, useCallback } from "react";
import "./Integratedlearningdiagram.css";

const hexData = [
  {
    id: "01",
    label: "Holistic Approach",
    color: "#f97316",
    position: "top",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" width="26" height="26">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 2a10 10 0 0 1 0 20" />
        <path d="M2 12h20" />
        <path d="M12 2c-2.5 3-4 6.5-4 10s1.5 7 4 10" />
      </svg>
    ),
  },
  {
    id: "02",
    label: "Technological Integration",
    color: "#e11d48",
    position: "top-right",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" width="26" height="26">
        <path d="M12 2L2 7l10 5 10-5-10-5z" />
        <path d="M2 17l10 5 10-5" />
        <path d="M2 12l10 5 10-5" />
      </svg>
    ),
  },
  {
    id: "03",
    label: "Learning Management System (LMS)",
    color: "#84cc16",
    position: "bottom-right",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" width="26" height="26">
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <path d="M8 21h8" /><path d="M12 17v4" /><path d="M9 10l2 2 4-4" />
      </svg>
    ),
  },
  {
    id: "04",
    label: "Adaptive Learning",
    color: "#06b6d4",
    position: "bottom",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" width="26" height="26">
        <path d="M9.5 2a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5z" />
        <path d="M14 8.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5z" />
        <path d="M7 14.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5z" />
        <path d="M11.5 4.5c1 1 2 2.5 2.5 4" />
        <path d="M14 13.5c-1 1-3 1.5-4.5 2" />
      </svg>
    ),
  },
  {
    id: "05",
    label: "Real-Time Analytics",
    color: "#eab308",
    position: "bottom-left",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" width="26" height="26">
        <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
      </svg>
    ),
  },
  {
    id: "06",
    label: "Accessibility",
    color: "#c026d3",
    position: "top-left",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" width="26" height="26">
        <circle cx="12" cy="5" r="2" />
        <path d="M12 22V12" /><path d="M5 9l7-2 7 2" /><path d="M5 9l2 6h10l2-6" />
      </svg>
    ),
  },
];

const angleMap = {
  "top":          -90,
  "top-right":    -30,
  "bottom-right":  30,
  "bottom":        90,
  "bottom-left":  150,
  "top-left":     210,
};

// ─── Breakpoint → canvas size & radii ───────────────────────────────────────
function getDimensions(canvasSize) {
  // Proportional ratios derived from original 600px design:
  // HEX_R = 140/600, LAB_R = 240/600, HEX_W = 108/600
  const s = canvasSize;
  return {
    SIZE:   s,
    CENTER: s / 2,
    HEX_R:  s * (140 / 600),
    LAB_R:  s * (240 / 600),
    HEX_W:  s * (108 / 600),
  };
}

function getCanvasSize() {
  const w = window.innerWidth;
  if (w >= 1200) return 600;
  if (w >= 992)  return 500;
  if (w >= 768)  return 420;
  if (w >= 480)  return 360;
  return 310;
}

// ─── Icon sizes per breakpoint ───────────────────────────────────────────────
function getIconSize(canvasSize) {
  if (canvasSize >= 600) return { hex: 26, label: 20, center: 30 };
  if (canvasSize >= 500) return { hex: 22, label: 18, center: 26 };
  if (canvasSize >= 420) return { hex: 18, label: 14, center: 22 };
  if (canvasSize >= 360) return { hex: 14, label: 12, center: 18 };
  return { hex: 12, label: 10, center: 15 };
}

// ─── Component ───────────────────────────────────────────────────────────────
export default function IntegratedLearningDiagram() {
  const [isVisible, setIsVisible]   = useState(false);
  const [pulseStep, setPulseStep]   = useState(0);
  const [activeId, setActiveId]     = useState(null);
  const [flip3D, setFlip3D]         = useState({});
  const [canvasSize, setCanvasSize] = useState(getCanvasSize);

  // ── Responsive resize listener ──
  useEffect(() => {
    const onResize = () => setCanvasSize(getCanvasSize());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ── Fade in ──
  useEffect(() => {
    const t = setTimeout(() => setIsVisible(true), 300);
    return () => clearTimeout(t);
  }, []);

  // ── Auto-pulse ──
  useEffect(() => {
    const interval = setInterval(() => {
      setPulseStep((s) => {
        const next = (s + 1) % hexData.length;
        const id = hexData[next].id;
        setFlip3D((prev) => ({ ...prev, [id]: true }));
        setTimeout(() => setFlip3D((prev) => ({ ...prev, [id]: false })), 900);
        return next;
      });
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const { SIZE, CENTER, HEX_R, LAB_R, HEX_W } = getDimensions(canvasSize);
  const iconSize = getIconSize(canvasSize);

  const pulsingId  = activeId ? null : hexData[pulseStep]?.id;
  const activeItem = activeId
    ? hexData.find((h) => h.id === activeId)
    : hexData[pulseStep];

  // Scaled ring sizes
  const r1 = SIZE * (310 / 600);
  const r2 = SIZE * (356 / 600);
  const r3 = SIZE * (176 / 600);
  const glowSize = SIZE * (340 / 600);

  // Center circle
  const centerSize   = SIZE * (158 / 600);
  const centerBorder = Math.max(1.5, SIZE * (3 / 600));

  // Line endpoints scale
  const lineInner = HEX_R + SIZE * (48 / 600);
  const lineOuter = LAB_R - SIZE * (42 / 600);

  return (
    <div className="ild-wrapper">
      <div
        className="ild-canvas"
        style={{ width: SIZE, height: SIZE, perspective: "900px" }}
      >
        {/* ── Ambient glow ── */}
        <div
          className="ild-glow"
          style={{
            width:      glowSize,
            height:     glowSize,
            background: `radial-gradient(circle, ${activeItem?.color || "#aaa"}22 0%, transparent 70%)`,
            transition: "background 0.9s ease",
            zIndex:     0,
          }}
        />

        {/* ── Ring 1 ── */}
        <div
          className="ild-ring ild-ring-1"
          style={{
            width:       r1,
            height:      r1,
            marginLeft: -r1 / 2,
            marginTop:  -r1 / 2,
          }}
        />

        {/* ── Ring 2 ── */}
        <div
          className="ild-ring ild-ring-2"
          style={{
            width:       r2,
            height:      r2,
            marginLeft: -r2 / 2,
            marginTop:  -r2 / 2,
            color:       `${activeItem?.color || "#bbb"}66`,
            transition:  "color 0.8s ease",
          }}
        />

        {/* ── Ring 3 ── */}
        <div
          className="ild-ring ild-ring-3"
          style={{
            width:       r3,
            height:      r3,
            marginLeft: -r3 / 2,
            marginTop:  -r3 / 2,
            color:       `${activeItem?.color || "#ddd"}44`,
            transition:  "color 0.8s ease",
          }}
        />

        {/* ── SVG connector lines ── */}
        <svg
          style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "visible" }}
          width={SIZE}
          height={SIZE}
        >
          <defs>
            {hexData.map((item) => (
              <filter
                key={`f-${item.id}`}
                id={`glow-${item.id}-${canvasSize}`}
                x="-50%" y="-50%"
                width="200%" height="200%"
              >
                <feGaussianBlur stdDeviation="4" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            ))}
          </defs>

          {hexData.map((item, i) => {
            const angle = (angleMap[item.position] * Math.PI) / 180;
            const x1 = CENTER + Math.cos(angle) * lineInner;
            const y1 = CENTER + Math.sin(angle) * lineInner;
            const x2 = CENTER + Math.cos(angle) * lineOuter;
            const y2 = CENTER + Math.sin(angle) * lineOuter;
            const isAct = pulsingId === item.id || activeId === item.id;
            return (
              <line
                key={item.id}
                x1={x1} y1={y1} x2={x2} y2={y2}
                stroke={item.color}
                strokeWidth={isAct ? 2.8 : 1.5}
                strokeDasharray={isAct ? "none" : "6 4"}
                opacity={isVisible ? (isAct ? 1 : 0.3) : 0}
                filter={isAct ? `url(#glow-${item.id}-${canvasSize})` : "none"}
                style={{ transition: `opacity 0.5s ease ${i * 0.1}s, stroke-width 0.4s` }}
              />
            );
          })}
        </svg>

        {/* ── Hexagons ── */}
        {hexData.map((item, i) => {
          const angle    = (angleMap[item.position] * Math.PI) / 180;
          const cx       = CENTER + Math.cos(angle) * HEX_R;
          const cy       = CENTER + Math.sin(angle) * HEX_R;
          const isAct    = pulsingId === item.id || activeId === item.id;
          const isFlipping = flip3D[item.id];

          return (
            <div
              key={item.id}
              className="ild-hex"
              onClick={() => setActiveId((p) => (p === item.id ? null : item.id))}
              style={{
                left:        cx,
                top:         cy,
                width:       HEX_W,
                height:      HEX_W,
                opacity:     isVisible ? 1 : 0,
                zIndex:      isAct ? 10 : 2,
                transition:  `opacity 0.5s ease ${i * 0.12}s`,
                perspective: "600px",
              }}
            >
              <div style={{
                width:          "100%",
                height:         "100%",
                transformStyle: "preserve-3d",
                transform: isFlipping
                  ? "rotateY(360deg) scale(1.22) translateZ(16px)"
                  : isAct
                    ? "rotateY(0deg) scale(1.2) translateZ(14px)"
                    : "rotateY(0deg) scale(1) translateZ(0px)",
                transition: isFlipping
                  ? "transform 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
                  : "transform 0.45s ease",
                filter: isAct
                  ? `drop-shadow(0 0 16px ${item.color}) drop-shadow(0 8px 22px ${item.color}70)`
                  : "drop-shadow(0 5px 12px rgba(0,0,0,0.3))",
              }}>
                <svg
                  viewBox="0 0 100 100"
                  width={HEX_W}
                  height={HEX_W}
                  style={{ display: "block" }}
                >
                  <defs>
                    <linearGradient id={`g-${item.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%"   stopColor={item.color}  stopOpacity="0.35" />
                      <stop offset="100%" stopColor="#080818"     stopOpacity="1" />
                    </linearGradient>
                    <linearGradient id={`shine-${item.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%"  stopColor="rgba(255,255,255,0.18)" />
                      <stop offset="50%" stopColor="rgba(255,255,255,0)" />
                    </linearGradient>
                  </defs>
                  <polygon points="50,6 94,29 94,71 50,94 6,71 6,29"
                    fill="rgba(0,0,0,0.35)" transform="translate(4,5)" />
                  <polygon points="50,6 94,29 94,71 50,94 6,71 6,29"
                    fill={`url(#g-${item.id})`}
                    stroke={item.color}
                    strokeWidth={isAct ? "3.5" : "2.5"} />
                  <polygon points="50,6 94,29 94,71 50,94 6,71 6,29"
                    fill={`url(#shine-${item.id})`} />
                  <polygon points="50,14 87,33 87,67 50,86 13,67 13,33"
                    fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
                  <polygon points="50,6 94,29 94,71 50,94 6,71 6,29"
                    fill="none" stroke={item.color} strokeWidth="9"
                    strokeDasharray="20 200" strokeDashoffset="-3" opacity="0.95" />
                  <text
                    x="50" y="62"
                    textAnchor="middle"
                    fill="white"
                    fontSize="31"
                    fontWeight="800"
                    fontFamily="'Courier New', monospace"
                    opacity="0.95"
                  >
                    {item.id}
                  </text>
                </svg>
              </div>
            </div>
          );
        })}

        {/* ── Labels ── */}
        {hexData.map((item, i) => {
          const angle  = (angleMap[item.position] * Math.PI) / 180;
          const cx     = CENTER + Math.cos(angle) * LAB_R;
          const cy     = CENTER + Math.sin(angle) * LAB_R;
          const isAct  = pulsingId === item.id || activeId === item.id;
          const labW   = HEX_W * (110 / 108);

          return (
            <div
              key={item.id}
              className="ild-label"
              style={{
                left:       cx,
                top:        cy,
                width:      labW,
                opacity:    isVisible ? 1 : 0,
                transition: `opacity 0.5s ease ${i * 0.12 + 0.35}s`,
              }}
            >
              <div className="ild-label-inner">
                <div
                  className="ild-label-icon"
                  style={{
                    width:      HEX_W * (40 / 108),
                    height:     HEX_W * (40 / 108),
                    background: isAct ? item.color + "28" : "rgba(240,240,240,0.6)",
                    border:     `2px solid ${isAct ? item.color : "#ccc"}`,
                    transform:  isAct ? "scale(1.3)" : "scale(1)",
                    boxShadow:  isAct ? `0 0 14px ${item.color}55` : "none",
                  }}
                >
                  {React.cloneElement(item.icon, {
                    width:  iconSize.label,
                    height: iconSize.label,
                    stroke: isAct ? item.color : "#999",
                  })}
                </div>
                <span
                  className="ild-label-text"
                  style={{
                    fontSize:   Math.max(6.5, HEX_W * (11.5 / 108)),
                    fontWeight: isAct ? 700 : 500,
                    color:      isAct ? item.color : "#555",
                    textShadow: isAct ? `0 0 10px ${item.color}55` : "none",
                  }}
                >
                  {item.label}
                </span>
              </div>
            </div>
          );
        })}

        {/* ── Center circle ── */}
        <div
          className="ild-center"
          style={{
            width:     centerSize,
            height:    centerSize,
            border:    `${centerBorder}px solid ${activeItem?.color || "#ddd"}70`,
            boxShadow: `
              0 10px 36px rgba(0,0,0,0.18),
              0 0 0 ${centerSize * 0.044}px ${activeItem?.color || "#eee"}28,
              0 0 0 ${centerSize * 0.088}px ${activeItem?.color || "#eee"}10,
              inset 0 2px 8px rgba(255,255,255,0.95)
            `,
          }}
        >
          <div className="ild-center-inner" style={{ padding: `0 ${centerSize * 0.09}px` }}>
            <div style={{ marginBottom: 6, display: "flex", justifyContent: "center" }}>
              {React.cloneElement(activeItem?.icon || hexData[0].icon, {
                width:  iconSize.center,
                height: iconSize.center,
                stroke: activeItem?.color || "#888",
              })}
            </div>
            <div
              style={{
                fontSize:   activeId
                  ? Math.max(6, centerSize * (9.5 / 158))
                  : Math.max(6.5, centerSize * (10.5 / 158)),
                fontWeight: 700,
                color:      activeItem?.color || "#333",
                lineHeight: 1.5,
                transition: "color 0.5s ease",
              }}
            >
              {activeId
                ? activeItem?.label
                : (
                  <>
                    <span style={{ fontSize: Math.max(6, centerSize * (10 / 158)) }}>What is an</span>
                    <br />Integrated<br />Learning<br />System?
                  </>
                )
              }
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}